/**
 * Data normalization functions for Dutch address and business data
 * These functions clean and format user input to match official formats
 */

/**
 * Normalize Dutch postal code
 * Removes spaces, converts to uppercase, validates format
 *
 * @param value - User input (e.g., "1234ab", "1234  AB", "1234 AB")
 * @returns Normalized value (e.g., "1234AB")
 *
 * @example
 * normalizeNLPostalCode("1234ab")     // "1234AB"
 * normalizeNLPostalCode("1234  AB")   // "1234AB"
 * normalizeNLPostalCode("1234 AB")    // "1234AB"
 */
export const normalizeNLPostalCode = (value: string): string => {
  if (!value) return ''

  // Remove all spaces, convert to uppercase
  const cleaned = value.replace(/\s/g, '').toUpperCase()

  // Return cleaned value (validation will check format separately)
  return cleaned
}

/**
 * Normalize BTW-ID (Dutch VAT number)
 * Removes spaces, converts to uppercase, adds NL prefix if missing
 *
 * @param value - User input (e.g., "nl123456789b01", "NL 123456789 B01", "123456789B01")
 * @returns Normalized value (e.g., "NL123456789B01")
 *
 * @example
 * normalizeBTWID("nl123456789b01")       // "NL123456789B01"
 * normalizeBTWID("NL 123456789 B01")     // "NL123456789B01"
 * normalizeBTWID("123456789B01")         // "NL123456789B01" (adds NL prefix)
 */
export const normalizeBTWID = (value: string): string => {
  if (!value) return ''

  // Remove spaces, convert to uppercase
  let cleaned = value.replace(/\s/g, '').toUpperCase()

  // Add NL prefix if missing and looks like Dutch format
  if (!cleaned.startsWith('NL')) {
    // Check if it matches the common pattern without NL prefix
    if (/^\d{9}B\d{2}$/.test(cleaned)) {
      cleaned = `NL${cleaned}`
    }
  }

  return cleaned
}

/**
 * Normalize international VAT number
 * Removes spaces, converts to uppercase
 *
 * @param value - User input (e.g., "be 0123456789", "DE 123456789")
 * @returns Normalized value (e.g., "BE0123456789", "DE123456789")
 *
 * @example
 * normalizeVATNumber("be 0123456789")    // "BE0123456789"
 * normalizeVATNumber("DE 123 456 789")   // "DE123456789"
 */
export const normalizeVATNumber = (value: string): string => {
  if (!value) return ''

  // Remove all spaces, convert to uppercase
  return value.replace(/\s/g, '').toUpperCase()
}

/**
 * Normalize KVK number (Dutch Chamber of Commerce number)
 * Removes all non-digit characters
 *
 * @param value - User input (e.g., "12 34 56 78", "12-34-56-78", "12345678")
 * @returns Normalized value (e.g., "12345678")
 *
 * @example
 * normalizeKVKNumber("12 34 56 78")    // "12345678"
 * normalizeKVKNumber("12-34-56-78")    // "12345678"
 * normalizeKVKNumber("12345678")       // "12345678"
 */
export const normalizeKVKNumber = (value: string): string => {
  if (!value) return ''

  // Remove all non-digit characters
  return value.replace(/\D/g, '')
}
